import { ReactNode } from 'react';
import { SpinnerType } from 'src/designSystem/Spinner';
import PageSpinner from 'src/designSystem/PageSpinner';
import { isInvalidIdError, isNotFoundError } from 'src/utils/queryErrors';

type GatedQuery = {
  data: unknown;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  refetch: () => unknown;
};

type LoadedData<TQueries extends readonly GatedQuery[]> = {
  [K in keyof TQueries]: NonNullable<TQueries[K]['data']>;
};

type Props<TQueries extends readonly GatedQuery[]> = {
  queries?: TQueries;
  isLoading?: boolean;
  isErrorShown?: boolean;
  errorFallback?: ReactNode | ((retry: () => void) => ReactNode);
  notFoundFallback?: ReactNode;
  spinnerType?: SpinnerType;
  renderSpinner?: (spinner: ReactNode) => ReactNode;
  renderData?: (...data: LoadedData<TQueries>) => ReactNode;
  children?: ReactNode | (() => ReactNode);
};

const hasNoData = (query: GatedQuery) =>
  query.data === undefined || query.data === null;

const isMissing = (query: GatedQuery) =>
  isNotFoundError(query.error) || isInvalidIdError(query.error);

function QueryGate<const TQueries extends readonly GatedQuery[]>({
  queries,
  isLoading = false,
  isErrorShown,
  errorFallback,
  notFoundFallback,
  spinnerType,
  renderSpinner,
  renderData,
  children = null,
}: Props<TQueries>) {
  const gated: readonly GatedQuery[] = queries ?? [];
  const failed = gated.filter(query => query.isError && hasNoData(query));
  const isNotFoundHandled = notFoundFallback !== undefined;
  const errored = isNotFoundHandled
    ? failed.filter(query => !isMissing(query))
    : failed;
  const retry = () => {
    errored.forEach(query => void query.refetch());
  };
  const isFailureHandled = errorFallback !== undefined;
  if (isErrorShown ?? (isFailureHandled && errored.length > 0)) {
    return (
      <>
        {typeof errorFallback === 'function'
          ? errorFallback(retry)
          : errorFallback}
      </>
    );
  }
  if (isNotFoundHandled && failed.some(isMissing)) {
    return <>{notFoundFallback}</>;
  }
  const isWaiting =
    isLoading || gated.some(query => query.isLoading && hasNoData(query));
  if (isWaiting) {
    const spinner = <PageSpinner type={spinnerType} />;
    return <>{renderSpinner ? renderSpinner(spinner) : spinner}</>;
  }
  if (renderData) {
    const data = gated.map(query => query.data);
    return (
      <>
        {data.every(value => value !== undefined && value !== null)
          ? renderData(...(data as LoadedData<TQueries>))
          : (notFoundFallback ?? null)}
      </>
    );
  }
  return <>{typeof children === 'function' ? children() : children}</>;
}

export default QueryGate;
